import { Table } from '@tanstack/react-table'
import { useLayoutEffect, useState } from 'react'

export function useColumnWidthsV2<TData>(
  table: Table<TData>,
  headerMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
  cellMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement[]>>
) {
  const [computedColWidths, setComputedColWidths] = useState<Record<string, number>>({})

  useLayoutEffect(() => {
    const next: Record<string, number> = {}

    const getMeasuredWidth = (el: HTMLDivElement | null): number => {
      if (!el) return 0
      // Prefer first child measurement so we track intrinsic content width
      // instead of full stretched cell width.
      const contentEl = el.firstElementChild
      if (contentEl instanceof HTMLElement) {
        return Math.ceil(contentEl.scrollWidth)
      }
      return Math.ceil(el.scrollWidth)
    }

    // Use getVisibleLeafColumns() to only calculate widths for visible columns
    // Measure ALL columns (including those with explicit size) so that
    // useFrozenColumnsV2 can use the measured width as a fallback when the
    // explicit size is smaller than the content + padding (e.g. the expander column).
    table.getVisibleLeafColumns().forEach((col) => {
      let maxContentWidth = 0

      // Measure header
      const headEl = headerMeasureRefs.current[col.id]
      maxContentWidth = Math.max(maxContentWidth, getMeasuredWidth(headEl))

      // Measure cells
      const cellEls = cellMeasureRefs.current[col.id] || []
      for (const el of cellEls) {
        if (!el) continue
        maxContentWidth = Math.max(maxContentWidth, getMeasuredWidth(el))
      }

      if (maxContentWidth > 0) {
        next[col.id] = maxContentWidth
      }
    })

    setComputedColWidths((prev) => {
      const changed =
        Object.keys(next).length !== Object.keys(prev).length || Object.keys(next).some((k) => next[k] !== prev[k])
      return changed ? next : prev
    })
  }, [table, headerMeasureRefs, cellMeasureRefs])

  return computedColWidths
}
